import { useMemo } from 'react'
import { type SessionResult } from '../components/sessionSummaryData'
import { FAMILY_DISPLAY_NAMES } from '../../content/familyLabels'
import { type AppState } from '../../app/store/types'
import { type GroupId } from '../../accounts/types'
import { useErrorPatterns } from '../../components/progress/ErrorPatternsSection'

/**
 * useSessionTip — session-specific Djay tip resolver (§20.4 G3).
 *
 * Picks ONE tip-template based on what actually happened in this session
 * and fills in concrete numbers / family names. No generic strings.
 *
 * Priority (first match wins):
 *  1. recordBroken      → "Sneller dan ooit op {family}"
 *  2. stageUpgrade      → "{family} groeit door: nu {newStage}"
 *  3. foutloos          → "Foutloos op {family}. Mooi gedaan."
 *  4. errorPattern≥2    → "Bij {family} ging 't {n}× mis. Tip: {hint}"
 *  5. streakMilestone   → "{n} dagen op rij. Doorzetten loont."
 *  6. labMode           → mode-specifieke quote
 *  7. reflection        → "Pittig setje. Bij {family} ging 't {n}× mis."
 *  8. steady-default    → "Stevige sessie op {family}. Door?"
 */

export interface SessionTipInput {
  sessionAnswers: SessionResult[]
  recordBroken: boolean
  stageUpgrades: { familyId: string; oldStage: number; newStage: number }[]
  foutloos: boolean
  streakMilestone: number | null
  labMode?: string
  isReflection: boolean
  state: AppState
  selectedGroup: GroupId
  /** True when 2+ of last 3 sessions were <50% — softens reflection copy. */
  repeatedStruggle?: boolean
}

export interface SessionTip {
  text: string
  emotion: string
}

const STAGE_LABELS = [
  '',
  'Net begonnen',
  'Aan het groeien',
  'Goed op weg',
  'Sterk',
  'Expert',
]

const STEADY_VARIANTS = [
  (family: string) => `Stevige sessie op ${family}. Door?`,
  (family: string) => `${family} zit er goed in. Volgende?`,
  (family: string) => `Lekker bezig op ${family}.`,
  (family: string) => `${family} — door op deze lijn?`,
]

function familyName(familyId: string | undefined): string {
  if (!familyId) return 'deze vragen'
  return FAMILY_DISPLAY_NAMES[familyId] ?? 'deze vragen'
}

function topPracticedFamily(answers: SessionResult[]): string | undefined {
  const counts: Record<string, number> = {}
  answers.forEach((a) => {
    if (a.familyId) counts[a.familyId] = (counts[a.familyId] ?? 0) + 1
  })
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1])
  return sorted[0]?.[0]
}

function topErrorFamily(answers: SessionResult[]): {
  familyId: string
  count: number
} | null {
  const counts: Record<string, number> = {}
  answers.forEach((a) => {
    if (!a.isCorrect && a.familyId) {
      counts[a.familyId] = (counts[a.familyId] ?? 0) + 1
    }
  })
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1])
  if (sorted.length === 0) return null
  return { familyId: sorted[0][0], count: sorted[0][1] }
}

function avgSeconds(answers: SessionResult[]): number {
  const total = answers.reduce((s, a) => s + (a.timeMs ?? 0), 0)
  if (total === 0 || answers.length === 0) return 0
  return Math.round(total / answers.length / 1000)
}

function pickVariant<T>(arr: T[], seed: number): T {
  return arr[seed % arr.length]
}

export function useSessionTip(input: SessionTipInput): SessionTip {
  const errorPatterns = useErrorPatterns(input.state, input.selectedGroup)

  return useMemo(() => {
    const {
      sessionAnswers,
      recordBroken,
      stageUpgrades,
      foutloos,
      streakMilestone,
      labMode,
      isReflection,
      repeatedStruggle,
    } = input

    const total = sessionAnswers.length
    const correct = sessionAnswers.filter((a) => a.isCorrect).length
    const topFamily = topPracticedFamily(sessionAnswers)
    const topFamilyName = familyName(topFamily)

    // 1. Record broken
    if (recordBroken) {
      const avg = avgSeconds(sessionAnswers)
      if (avg > 0 && labMode === 'fluency') {
        return {
          text: `Sneller dan ooit op ${topFamilyName}: ${avg} sec per vraag.`,
          emotion: 'celebrating',
        }
      }
      return {
        text: `Beste score op ${topFamilyName} ooit — ${correct} van de ${total}!`,
        emotion: 'celebrating',
      }
    }

    // 2. Stage upgrade
    if (stageUpgrades.length > 0) {
      const up = stageUpgrades[0]
      const stageLabel = STAGE_LABELS[up.newStage] ?? 'sterker'
      return {
        text: `${familyName(up.familyId)} groeit door: nu ${stageLabel}.`,
        emotion: 'proud',
      }
    }

    // 3. Foutloos
    if (foutloos) {
      if (total >= 8) {
        return {
          text: `${total} vragen, álles goed op ${topFamilyName}. Sterk.`,
          emotion: 'cheering',
        }
      }
      return {
        text: `Foutloos op ${topFamilyName}. Mooi gedaan.`,
        emotion: 'cheering',
      }
    }

    // 4. Error pattern (only if substantial: ≥2 errors)
    const sessionErr = topErrorFamily(sessionAnswers)
    if (sessionErr && sessionErr.count >= 2) {
      // Try to enrich with global error-pattern hint
      const globalHint = errorPatterns.find(
        (p) => p.familyId === sessionErr.familyId,
      )
      if (globalHint) {
        return {
          text: `Bij ${familyName(sessionErr.familyId)} ging 't ${sessionErr.count}× mis. Tip: ${globalHint.recommendation}`,
          emotion: 'thinking',
        }
      }
      return {
        text: `In ${familyName(sessionErr.familyId)} valt nog winst te halen — ${sessionErr.count}× hetzelfde patroon.`,
        emotion: 'thinking',
      }
    }

    // 5. Streak milestone
    if (streakMilestone) {
      return {
        text: `${streakMilestone} dagen op rij. Doorzetten loont.`,
        emotion: 'celebrating',
      }
    }

    // 6. Lab mode defaults
    if (labMode === 'fluency') {
      return {
        text: 'Tempo zit erin — herhaling maakt het automatisch.',
        emotion: 'happy',
      }
    }
    if (labMode === 'challenge') {
      return {
        text: 'Chapeau dat je deze uitdaging koos.',
        emotion: 'proud',
      }
    }
    if (labMode === 'sprint') {
      return {
        text: `Sprint op ${topFamilyName} afgerond.`,
        emotion: 'happy',
      }
    }

    // 7. Reflection (low score)
    if (isReflection) {
      // 7a. Repeated struggle — soften, no error-count, no pressure (§20.7 G6)
      if (repeatedStruggle) {
        return {
          text: `${topFamilyName} voelt nog wat lastig. Geen druk — kleine stapjes werken hier het best.`,
          emotion: 'supportive',
        }
      }
      if (sessionErr) {
        return {
          text: `Pittig setje. Bij ${familyName(sessionErr.familyId)} ging 't ${sessionErr.count}× mis.`,
          emotion: 'supportive',
        }
      }
      return {
        text: 'Vandaag liep het niet, dat hoort erbij. Morgen vers begin.',
        emotion: 'supportive',
      }
    }

    // 8. Steady default — variant based on session length as cheap seed
    const variant = pickVariant(STEADY_VARIANTS, total + correct)
    return {
      text: variant(topFamilyName),
      emotion: 'friendly',
    }
  }, [input, errorPatterns])
}
